import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { langLabel, langFlag } from "@/lib/langLabel";
import { ShieldAlert, ShieldCheck, Fingerprint, Globe, ChevronDown, Ban, Undo2, Coins, FileStack, Crown, Trash2, UserCheck } from "lucide-react";

const initials = (name) => (name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const SIGNAL_LABELS = { device: "même appareil", email_alias: "alias email", ip: "même IP" };

const ActBtn = ({ testid, onClick, icon: Icon, label, cls }) => (
  <button data-testid={testid} onClick={onClick} title={label}
    className={`px-2 h-8 border rounded-sm font-mono text-[10px] uppercase tracking-widest transition-colors inline-flex items-center gap-1 shrink-0 ${cls}`}>
    <Icon className="w-3.5 h-3.5" />
  </button>
);

const MemberRow = ({ m, group, onBan, onValidate, onReset, onDelete, onKeep }) => (
  <div data-testid={`linked-member-${m.user_id}`} className="flex items-center gap-3 py-2.5 border-b border-white/5 last:border-0">
    {m.picture
      ? <img src={m.picture} alt="" className="w-8 h-8 rounded-full shrink-0" />
      : <div className="w-8 h-8 rounded-full bg-aurora-cyan/20 grid place-items-center text-aurora-cyan font-bold text-xs shrink-0">{initials(m.name)}</div>}
    <div className="min-w-0 flex-1">
      <div className="text-sm truncate flex items-center gap-2">
        {m.name}
        {m.is_owner && <span className="font-mono text-[9px] uppercase text-aurora-purple inline-flex items-center gap-1"><Crown className="w-3 h-3" />Owner</span>}
        {m.abuse_flag && (
          <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-signal-error/40 text-signal-error bg-signal-error/5">
            {(m.abuse_signals || []).map((s) => SIGNAL_LABELS[s] || s).join(", ") || "Signalé"}
          </span>
        )}
        {m.verified && !m.abuse_flag && <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm border border-signal-success/40 text-signal-success bg-signal-success/5 inline-flex items-center gap-1"><ShieldCheck className="w-3 h-3" />Vérifié</span>}
        {m.banned && <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-signal-error text-white">Suspendu</span>}
      </div>
      <div className="font-mono text-[10px] text-zinc-500 truncate flex items-center gap-2">
        {m.email}
        {m.lang && <span title={`Langue : ${langLabel(m.lang)}`} className="text-zinc-400">{langFlag(m.lang)} {m.lang.toUpperCase()}</span>}
      </div>
    </div>
    <div className="hidden sm:flex items-center gap-3 font-mono text-[11px] text-zinc-400 shrink-0">
      <span className="inline-flex items-center gap-1 text-aurora-cyan"><Coins className="w-3 h-3" />{m.credits}</span>
      <span className="inline-flex items-center gap-1"><FileStack className="w-3 h-3" />{m.orders_count}</span>
      <span className="text-signal-success">{m.total_spent}€</span>
    </div>
    {!m.is_owner && (
      <div className="flex items-center gap-1.5 shrink-0">
        {group.count >= 2 && (
          <ActBtn testid={`keep-${m.user_id}`} onClick={() => onKeep(m, group)} icon={UserCheck} label="Garder celui-ci (nettoie les autres du groupe)"
            cls="border-aurora-cyan/40 text-aurora-cyan hover:bg-aurora-cyan hover:text-black" />
        )}
        {m.abuse_flag && (
          <ActBtn testid={`validate-${m.user_id}`} onClick={() => onValidate(m)} icon={ShieldCheck} label="Valider (lever le drapeau)"
            cls="border-signal-success/40 text-signal-success hover:bg-signal-success hover:text-white" />
        )}
        <ActBtn testid={`reset-credits-${m.user_id}`} onClick={() => onReset(m)} icon={Coins} label="Remettre les crédits à 0"
          cls="border-yellow-400/40 text-yellow-400 hover:bg-yellow-400 hover:text-black" />
        {m.banned ? (
          <button data-testid={`unban-${m.user_id}`} onClick={() => onBan(m, false)}
            className="px-2 h-8 border border-white/15 rounded-sm font-mono text-[10px] uppercase tracking-widest hover:border-signal-success hover:text-signal-success transition-colors inline-flex items-center gap-1 shrink-0">
            <Undo2 className="w-3.5 h-3.5" />
          </button>
        ) : (
          <ActBtn testid={`ban-${m.user_id}`} onClick={() => onBan(m, true)} icon={Ban} label="Suspendre"
            cls="border-signal-error/30 text-signal-error hover:bg-signal-error hover:text-white" />
        )}
        <ActBtn testid={`delete-${m.user_id}`} onClick={() => onDelete(m)} icon={Trash2} label="Supprimer le compte"
          cls="border-signal-error/30 text-signal-error hover:bg-signal-error hover:text-white" />
      </div>
    )}
  </div>
);

export const LinkedAccountsPanel = ({ onChanged }) => {
  const [data, setData] = useState({ groups: [], total_groups: 0, flagged_users: 0 });
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/linked-accounts").then(({ data }) => setData(data)).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const ban = async (m, banned) => {
    if (banned && !window.confirm(`Suspendre le compte de ${m.name} (${m.email}) ?\n\nSes sessions seront fermées et il ne pourra plus se connecter.`)) return;
    const reason = banned ? (window.prompt("Motif de la suspension (optionnel) :") ?? "") : "";
    try {
      await api.post(`/admin/users/${m.user_id}/ban`, { banned, reason });
      toast.success(banned ? "Compte suspendu" : "Compte réactivé");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const validate = async (m) => {
    if (!window.confirm(`Valider le compte ${m.email} comme légitime ?\n\nLe drapeau anti-fraude sera levé.`)) return;
    const restore = !m.welcome_credited && window.confirm("Restaurer aussi les crédits de bienvenue offerts (geste commercial) ?");
    try {
      const { data } = await api.post(`/admin/users/${m.user_id}/clear-flag`, { restore_credits: restore });
      toast.success(data.restored_credits ? `Validé · +${data.restored_credits} crédits restaurés` : "Compte validé");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const resetCredits = async (m) => {
    if (!window.confirm(`Remettre les crédits de ${m.email} à 0 ?\n\nLe compte est conservé, mais le bonus de bienvenue est bloqué.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${m.user_id}/reset-credits`);
      toast.success(`Crédits remis à 0 (${data.removed} retirés)`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const deleteUser = async (m) => {
    if (!window.confirm(`SUPPRIMER définitivement le compte de ${m.name} (${m.email}) ?\n\nLes commandes sont conservées pour l'historique. Cette action est irréversible.`)) return;
    try {
      await api.delete(`/admin/users/${m.user_id}`);
      toast.success("Compte supprimé (commandes conservées)");
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const keepThisOne = async (m, group) => {
    const others = group.members.filter((x) => x.user_id !== m.user_id && !x.is_owner);
    if (!window.confirm(`Garder « ${m.name} » comme compte légitime et remettre les crédits des ${others.length} autre(s) compte(s) du groupe à 0 ?`)) return;
    try {
      await api.post(`/admin/users/${m.user_id}/clear-flag`, {});
      await Promise.all(others.map((x) => api.post(`/admin/users/${x.user_id}/reset-credits`).catch(() => null)));
      toast.success(`« ${m.name} » validé · ${others.length} doublon(s) neutralisé(s)`);
      load(); onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  if (!loading && data.total_groups === 0) return null;

  return (
    <div className="glass rounded-md mb-6 overflow-hidden border border-signal-error/20" data-testid="linked-accounts-panel">
      <button
        data-testid="linked-accounts-toggle"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-5 py-4 hover:bg-white/5 transition-colors text-left"
      >
        <div className="w-10 h-10 grid place-items-center rounded-sm bg-signal-error/10 border border-signal-error/30 shrink-0">
          <ShieldAlert className="w-5 h-5 text-signal-error" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display text-sm font-bold uppercase tracking-tight">Comptes liés · Anti-fraude</div>
          <div className="font-mono text-[11px] text-zinc-400">
            {data.total_groups} groupe(s) partageant appareil/IP · {data.flagged_users} compte(s) signalé(s)
          </div>
        </div>
        <ChevronDown className={`w-5 h-5 text-zinc-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-5 pb-5 space-y-4">
          {data.groups.map((g, i) => (
            <div key={`${g.type}-${g.key}`} data-testid={`linked-group-${i}`} className="rounded-sm border border-white/10 bg-black/20">
              <div className="flex items-center gap-2 px-4 py-2.5 border-b border-white/10">
                {g.type === "device" ? <Fingerprint className="w-4 h-4 text-aurora-cyan" /> : <Globe className="w-4 h-4 text-aurora-purple" />}
                <span className="font-mono text-[11px] uppercase tracking-widest text-zinc-300">
                  {g.type === "device" ? "Même appareil" : "Même IP"}
                </span>
                <span className="font-mono text-[10px] text-zinc-600 truncate">{g.key_short}</span>
                <span className="ml-auto font-mono text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm bg-signal-error/15 text-signal-error">{g.count} comptes</span>
              </div>
              <div className="px-4">
                {g.members.map((m) => (
                  <MemberRow key={m.user_id} m={m} group={g} onBan={ban}
                    onValidate={validate} onReset={resetCredits} onDelete={deleteUser} onKeep={keepThisOne} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
